import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useMediaQuery, useTheme } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { Theme } from "@material-ui/core";
import { useAtomicStyles } from "styles";
import { useDispatch, useSelector } from "react-redux";
import { actions, selectors } from "redux-store";
import moment from "moment";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/dist/yup";
import * as yup from "yup";
import { useCurrentMerchantId, useTypedTranslations } from "hooks";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogContent: {
      display: "flex",
      flexDirection: "column",
      textAlign: "center",
      height: 420,
      [theme.breakpoints.down("xs")]: {
        height: 350,
        padding: 1,
      },
    },
    dialogAvatar: {
      width: 100,
      height: 100,
      backgroundColor: theme.palette.primary.main,
      margin: "-50px auto 0 auto",
    },
    closeButton: {
      position: "relative",
      background: "white",
      transitionDuration: "0.2s",
      left: "95%",
      top: -45,
      [theme.breakpoints.down("xs")]: {
        left: "91%",
      },
      "&:hover": {
        background: "#673368",
        color: "white",
      },
      height: 10,
      width: 10,
    },
    dialogIcon: {
      width: 70,
      height: 70,
    },
    dialog: {
      overflow: "visible",
      marginTop: "5em",
    },
    dropDown: {
      width: "75%",
      left: 75,
      maxHeight: 300,
      [theme.breakpoints.down("xs")]: {
        maxHeight: 150,
        width: "85%",
        left: 20,
      },
    },
    inputProps: {
      width: "80%",
    },
    searchBar: {
      marginBottom: "2em",
    },
    inputsRoot: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    inputContainer: {
      width: "80%",
      display: "flex",
      flexDirection: "column",
    },
    buttonsContainer: {
      [theme.breakpoints.down("xs")]: {
        marginTop: "2em",
      },
      display: "flex",
      width: "100%",
      flexDirection: "row",
      justifyContent: "space-evenly",
    },
  }),
);

const schema = yup.object().shape({
  title: yup
    .string()
    .required("Campo obbligatorio")
    .min(3, "Minimo 3 caratteri")
    .max(30, "Massimo 30 caratteri"),
  description: yup
    .string()
    .required("Campo obbligatorio")
    .min(10, "Minimo 10 caratteri")
    .max(100, "Massimo 100 caratteri"),
  eventId: yup.string().required(),
});

const useSendNotificationDialog = () => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const classes = useMemo(
    () => ({
      dialogPaper: {
        paper: styles.dialog,
      },
    }),
    [styles],
  );
  const atomicStyles = useAtomicStyles();
  const {
    register,
    handleSubmit,
    errors,
    watch,
    setValue,
    formState: { isValid },
    setError,
  } = useForm<{
    title: string;
    description: string;
    eventId: string;
  }>({
    resolver: yupResolver(schema),
    mode: "all",
  });
  const [placeholder, setPlaceHolder] = useState("Cerca tra i tuoi eventi");
  const [eventTitle, setEventTitle] = useState("");
  const merchantId = useCurrentMerchantId();
  const isLoading = useSelector(
    selectors.getAjaxIsLoadingByApi(actions.postNotifications.api),
  );
  const dispatch = useDispatch();
  const open = useSelector(selectors.isNotificationDialogOpen);
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  const events = useSelector(selectors.getEventsList).filter((ev) => {
    return moment(ev.expire).isAfter(moment());
  });
  const [t] = useTypedTranslations();

  const handleDialogClose = useCallback(() => {
    dispatch(actions.setIsNotificationDialogOpen(false));
  }, [dispatch]);

  const eventData = useMemo(() => {
    return events.filter((ev) => {
      return ev.title
        .toLocaleLowerCase()
        .includes(eventTitle.toLocaleLowerCase());
    });
  }, [events, eventTitle]);

  const handleSelection = useCallback(
    (eventId: string) => {
      setValue("eventId", eventId, { shouldValidate: true });
      const event = events.find((e) => e._id === eventId);
      setEventTitle(event?.title);
    },
    [setValue, events],
  );

  const options: { label: string; value: string | number }[] = useMemo(() => {
    return eventData.map((ed) => ({
      label: ed.title,
      value: ed._id,
    }));
  }, [eventData]);

  const onSubmit = useCallback(
    (isTest) =>
      handleSubmit((formData) => {
        if (!isTest) {
          if (!window.confirm("sei sicuro di voler procedere?")) return;
        }

        const payload = {
          title: formData.title,
          description: formData.description,
          eventId: formData.eventId,
          merchantId,
          isTest,
        };
        dispatch(actions.postNotifications.request(payload));
      }),
    [handleSubmit, dispatch, merchantId],
  );

  const onEventTextChange = useCallback((ev) => {
    setEventTitle(ev.target.value);
  }, []);

  useEffect(() => {
    register("eventId");
  }, [register]);

  return {
    styles,
    classes,
    atomicStyles,
    open,
    handleDialogClose,
    eventData,
    isMobile,
    options,
    register,
    errors,
    watch,
    onSubmit,
    handleSelection,
    isValid,
    placeholder,
    isLoading,
    t,
    eventTitle,
    onEventTextChange,
  };
};

export { useSendNotificationDialog as default };
